import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { components } from '@src/types/schema';
import { StyledMarkdown } from '@components/atoms/StyledMarkdown';

type AnnouncementSection = components['schemas']['AnnouncementSectionDto'];

export function AnnouncementBar({
  section,
  innerClassName = '',
}: {
  section: AnnouncementSection | undefined;
  /**
   * Classes for the inner content wrapper, used to align the text with the
   * page content on desktop while the colored bar itself spans full width
   * (e.g. "max-w-[1300px] mx-auto px-4").
   */
  innerClassName?: string;
}) {
  const { t } = useTranslation('component', { keyPrefix: 'announcementBar' });
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const heading = section?.heading?.trim();
  const body = section?.body?.trim();
  const hasContent = !!(heading || body);

  // Detect whether the (collapsed) body actually exceeds the 2-line clamp, so
  // the "Read more" button only appears when there is more to reveal.
  useLayoutEffect(() => {
    const element = bodyRef.current;
    if (!element || !body) {
      setIsOverflowing(false);
      return;
    }

    const measure = () => {
      // Only meaningful while collapsed: compare full content height against the
      // clamped visible height.
      if (expanded) return;
      setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [body, expanded]);

  // A section is shown only when it is enabled AND has text (heading or body).
  if (!section?.enabled || !hasContent) {
    return null;
  }

  return (
    <div
      className="w-full bg-error text-white"
      data-testid="AnnouncementBarComponent"
      role="status"
    >
      {/*
        Grid so the body can align differently per breakpoint:
        - mobile: body sits in column 2, aligned under the heading
        - desktop (sm+): body spans both columns from the icon's left edge
      */}
      <div
        className={`grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-0.5 items-start py-2.5 ${innerClassName}`}
      >
        <span
          aria-hidden="true"
          className="shrink-0 text-xl leading-none mt-0.5"
        >
          ⚠️
        </span>
        {heading && (
          <p className="min-w-0 font-extrabold text-sm uppercase tracking-wide">
            {heading}
          </p>
        )}
        {body && (
          <div className="col-start-2 sm:col-start-1 sm:col-span-2 min-w-0">
            <div
              ref={bodyRef}
              // Links inside the announcement keep the white text color, but are
              // underlined and semibold so they read as links on the bar.
              className={`text-sm leading-snug [&_a]:text-white [&_a]:font-semibold [&_a]:underline ${expanded ? '' : 'line-clamp-2'}`}
            >
              <StyledMarkdown markdown={body} />
            </div>
            {(isOverflowing || expanded) && (
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="mt-0.5 text-sm font-bold text-white underline"
                data-testid="AnnouncementBarToggleComponent"
              >
                {expanded ? t('readLess') : t('readMore')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
